import React, {useState} from "react";
import {toast} from "sonner";
import FormLoader from "@/Components/loader";
import ReactGA from "react-ga4";
import PatternBg from "../assets/Assets/Svgs/Pattern.svg";
import profileIcon from "../assets/Assets/Svgs/profileIcon.svg";
import success from '../assets/Assets/Images/success-check.png';
import cation from '../assets/Assets/Svgs/CautionIcon.svg';
import {Link} from 'react-router-dom';


import Header from "../Components/Auth/Header";
import Title from "../Components/Title";
import {checkEmail, submitPartnerForm} from "@/Api";

const GROUP_OPTIONS = [
    {label: "Hospital", value: "hospital"},
    {label: "University", value: "university"},
];

const PartnerSignup = () => {

    const [isModalOpen, setModalOpen] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');


    React.useEffect(() => {
        ReactGA.send({hitType: "pageview", page: window.location.pathname});
    }, []);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        company_name: "",
        company_address: "",
        company_phone: "",
        crisis_email: "",
        prefix_code: "",
        group: "",
    });

    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const isValidEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
    };

    const validateStep1 = () => {
        if (!formData.name.trim()) {
            toast.error("Name is required");
            return false;
        }
        if (!isValidEmail(formData.email)) {
            toast.error("Valid email is required");
            return false;
        }
        if (!formData.group) {
            toast.error("Please select a group");
            return false;
        }
        return true;
    };

    const validateStep2 = () => {
        if (!formData.company_name.trim()) {
            toast.error("Company name is required");
            return false;
        }
        if (!formData.company_address.trim()) {
            toast.error("Company address is required");
            return false;
        }
        if (!formData.company_phone.trim()) {
            toast.error("Company phone is required");
            return false;
        }
        if (!isValidEmail(formData.crisis_email)) {
            toast.error("Valid crisis email is required");
            return false;
        }
        return true;
    };

    // Step 1: Next button handler
    const handleNext = async (e) => {
        e.preventDefault();

        if (!validateStep1()) return;

        setIsLoading(true);
        try {
            const response = await checkEmail({email: formData.email});
            if (response.data.success) {
                toast.error("Email already in use");
                setIsLoading(false);
            }
        } catch (error) {
            if (
                error?.response?.status &&
                error?.response?.data?.message === "Invalid data"
            ) {
                // Email does not exist, proceed to step 2
                setCurrentStep(2);
            } else {
                toast.error("Something went wrong");
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Step 2: Submit button handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateStep2()) return;
        setIsLoading(true);

        try {
            const res = await submitPartnerForm(formData);
            if (res.data?.success) {
                setFormData({
                    name: "",
                    email: "",
                    company_name: "",
                    company_address: "",
                    company_phone: "",
                    crisis_email: "",
                    prefix_code: "",
                    group: "",
                });
                setCurrentStep(1);
                setIsError(false);
            } else {
                setIsError(true);
                setErrorMessage("Something went wrong.");
            }
        } catch (err) {
            setIsError(true);
            const errors = err?.response?.data?.errors;
            const defaultMessage = "Something went wrong. Please try again.";

            if (errors && typeof errors === "object") {
                const firstKey = Object.keys(errors)[0];
                const firstError = errors[firstKey]?.[0];
                setErrorMessage(firstError || defaultMessage);
            } else {
                setErrorMessage(err?.response?.data?.message || defaultMessage);
            }
        } finally {
            setModalOpen(true);
            setIsLoading(false);
        }
    };

    return (
        <div
            className="h-[100%] bg-cover sign-in min-h-screen flex flex-col bg-[#FFFDF2] relative px-[5%] py-[1%] pb-6 lg:pb-2 max-[767px]:py-[5%] max-[767px]:min-h-[100vh]"
            style={{backgroundImage: `url(${PatternBg})`}}
        >
            <Header setStep={setCurrentStep} step={currentStep}/>
            <Title title={"Partner Signup"}/>

            <div
                className="h-[16px] p-[4px] mx-auto bg-white shadow-lg rounded-full laptop:mt-[2.5%] max-[767px]:mt-[14%] tablet:mt-[10vh] w-full tablet:w-[60vw] tablet:min-w-[520px] tablet:max-w-[522px] lg:mb-5"
            >
                <div
                    className="h-full rounded-full bg-[#1A6A73]"
                    style={{
                        width: currentStep === 1 ? "50%" : "100%",
                        transition: "width 0.5s ease",
                    }}
                />
            </div>

            <form
                onSubmit={currentStep === 1 ? handleNext : handleSubmit}
                className="flex flex-col mt-3 items-center shadow-lg bg-white pb-8 rounded-[20px] z-10 max-[767px]:w-[100%] max-[767px]:h-[7%] max-[1520px]:mb-[5%] tablet:h-[700px] overflow-y-auto tablet:w-[60vw] mx-auto tablet:min-w-[520px] tablet:max-w-[522px]"
            >

                <div className="pt-[8%] laptop:pt-6 text-center flex flex-col items-center border-b-[1px] border-[#E2E4E9] w-[90%] pb-[5%]">
                    <img alt="profileIcon" src={profileIcon}/>
                    <h1 className="text-[24px] font-bold max-[767px]:text-[20px]">
                        Sign up as a Partner
                    </h1>
                    <p>
                        {currentStep === 1
                            ? "Enter your basic information"
                            : "Enter your company details"}
                    </p>
                </div>

                <div className="max-[767px]:w-[90%]">
                    {currentStep === 1 ? (
                        <>
                            {/* Name */}
                            <div className="my-6 bg-[#F9F7F3] w-[450px] text-[13px] py-[3%] px-[5%] rounded-[30px] max-[1130px]:rounded-[20px] max-[767px]:w-[100%] max-[767px]:rounded-[15px]">
                                <p className="text-[15px] font-semibold text-[#191B1E]">Name</p>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter Full Name"
                                    className="bg-[#F9F7F3] text-[15px] font-semibold w-[100%] h-[30px] max-[767px]:text-[14px]"

                                />
                            </div>

                            {/* Email */}
                            <div className="bg-[#F9F7F3] w-[450px] text-[13px] mt-[3%] py-[3%] px-[5%] rounded-[30px] max-[1130px]:rounded-[20px] max-[767px]:w-[100%] max-[767px]:rounded-[15px]">
                                <p className="text-[15px] font-semibold text-[#191B1E]">
                                    Email Address
                                </p>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Enter email address"
                                    className="bg-[#F9F7F3] text-[15px] font-semibold w-[100%] h-[30px] max-[767px]:text-[14px]"

                                />
                            </div>

                            {/* Group Select */}
                            <div className="bg-[#F9F7F3] w-[450px] text-[13px] mt-[3%] py-[3%] px-[5%] rounded-[30px] max-[1130px]:rounded-[20px] max-[767px]:w-[100%] max-[767px]:rounded-[15px]">
                                <label
                                    htmlFor="group"
                                    className="text-[15px] font-semibold text-[#191B1E]"
                                >
                                    Category
                                </label>
                                <select
                                    name="group"
                                    id="group"
                                    value={formData.group}
                                    onChange={handleChange}
                                    className="bg-[#F9F7F3] text-[15px] font-semibold w-full h-[40px] min-h-[40px] relative z-10"
                                >
                                    <option value="" disabled>
                                        Select category
                                    </option>
                                    {GROUP_OPTIONS.map(({label, value}) => (
                                        <option key={value} value={value} className="mt-2">
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type="submit"
                                className="bg-[#1A6A73] w-[450px] text-white rounded-[30px] font-semibold py-3 mt-6 max-[767px]:w-[100%]"
                                disabled={isLoading}
                            >
                                Next
                            </button>
                            <div className="p-2">
                                {isLoading ? <FormLoader/> : ""}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Company Name */}
                            <div className="bg-[#F9F7F3] w-[450px] text-[13px] mt-[3%] py-[3%] px-[5%] rounded-[30px] max-[1130px]:rounded-[20px] max-[767px]:w-[100%] max-[767px]:rounded-[15px]">
                                <p className="text-[15px] font-semibold text-[#191B1E]">
                                    Company Name
                                </p>
                                <input
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleChange}
                                    placeholder="Enter company name"
                                    className="bg-[#F9F7F3] text-[15px] font-semibold w-[100%] h-[30px] max-[767px]:text-[14px]"

                                />
                            </div>

                            {/* Company Address */}
                            <div className="bg-[#F9F7F3] w-[450px] text-[13px] mt-[3%] py-[3%] px-[5%] rounded-[30px] max-[1130px]:rounded-[20px] max-[767px]:w-[100%] max-[767px]:rounded-[15px]">
                                <p className="text-[15px] font-semibold text-[#191B1E]">
                                    Company Address
                                </p>
                                <input
                                    type="text"
                                    name="company_address"
                                    value={formData.company_address}
                                    onChange={handleChange}
                                    placeholder="Enter company address"
                                    className="bg-[#F9F7F3] text-[15px] font-semibold w-[100%] h-[30px] max-[767px]:text-[14px]"

                                />
                            </div>

                            {/* Company Phone */}
                            <div className="bg-[#F9F7F3] w-[450px] text-[13px] mt-[3%] py-[3%] px-[5%] rounded-[30px] max-[1130px]:rounded-[20px] max-[767px]:w-[100%] max-[767px]:rounded-[15px]">
                                <p className="text-[15px] font-semibold text-[#191B1E]">
                                    Company Phone
                                </p>
                                <input
                                    type="tel"
                                    name="company_phone"
                                    value={formData.company_phone}
                                    onChange={handleChange}
                                    placeholder="+971501234567"
                                    // pattern="^\+9715[0-9]{8}$"
                                    className="bg-[#F9F7F3] text-[15px] font-semibold w-[100%] h-[30px] max-[767px]:text-[14px]"
                                />
                            </div>

                            {/* Crisis Email */}
                            <div className="bg-[#F9F7F3] w-[450px] text-[13px] mt-[3%] py-[3%] px-[5%] rounded-[30px] max-[1130px]:rounded-[20px] max-[767px]:w-[100%] max-[767px]:rounded-[15px]">
                                <p className="text-[15px] font-semibold text-[#191B1E]">
                                    Crisis Email
                                </p>
                                <input
                                    type="email"
                                    name="crisis_email"
                                    value={formData.crisis_email}
                                    onChange={handleChange}
                                    placeholder="Enter crisis email"
                                    className="bg-[#F9F7F3] text-[15px] font-semibold w-[100%] h-[30px] max-[767px]:text-[14px]"
                                />
                            </div>

                            <div className="flex justify-between w-[450px] max-[767px]:w-[100%] mt-6 mb-1">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="bg-gray-400 text-white rounded-[30px] font-semibold py-3 px-10 max-[767px]:w-[40%]"
                                >
                                    Back
                                </button>

                                <button
                                    type="submit"
                                    className="bg-[#1A6A73] text-white rounded-[30px] font-semibold py-3 px-10 max-[767px]:w-[40%]"
                                >
                                    Submit
                                </button>
                            </div>
                            {isLoading ? <FormLoader/> : ""}
                        </>
                    )}
                </div>
            </form>


            {isModalOpen && (
                <div className="modal-overlay modal-overlay-color">
                    <div className="modal">
                        <div className="w-[100%] flex cursor-pointer justify-end">
                            <button
                                onClick={() => setModalOpen(false)}
                                className="btn-close"
                            >
                                <span className="icon-cross"></span>
                            </button>
                        </div>

                        <img
                            className="w-[70px]"
                            alt={isError ? "error" : "success"}
                            src={isError ? cation : success}
                        />

                        <div className="w-[90%] text-center my-[3%]">
                            <h1 className="text-[#0A090B] font-semibold text-[20px] mb-[3%]">
                                {isError ? "Submission Failed" : "Application Submitted!"}
                            </h1>
                            <p className="text-[#7F7D83]">
                                {isError
                                    ? errorMessage
                                    : `Thank you for signing up as a partner. We’ve received your information and will contact you shortly.`}
                            </p>
                        </div>

                        <div>
                            <Link to={'/'}>
                                <button
                                    type="button"
                                    className="bg-[#1A6A73] text-center w-[300px] py-[4%] mt-[5%] text-[white] rounded-[30px]"
                                >
                                    Go to Homepage
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );

};

export default PartnerSignup;